(ns app.front.view.property.dashboard.widgets.search
  (:require ["@carbon/icons-react/es/add/16" :default Add16]
            [app.domain.property.model :as property-model]
            [app.domain.util :as du]
            [app.front.dashboard.map :as dashboard-map]
            [app.front.municipality :as municipality]
            [app.front.search-counts :as search-counts]
            [app.front.search-result :as sr]
            [app.front.ui :as ui]
            [app.front.ui.filter-common :as filter-common]
            [app.front.ui.modal :as ui-modal]
            [app.front.ui.multi-select :as ui-multi-select]
            [app.front.ui.sortable-table :as sortable-table]
            [app.front.util :as futil]
            [app.front.view.admin.common :as acommon]
            [app.front.view.periodic-inspection.new-periodic-inspection :as new-periodic-inspection]
            [app.front.view.property.dashboard.subs :as property-dashboard-subs]
            [app.front.view.property.dashboard.widgets.filters :as property-search-filters]
            [app.front.view.search.saved-searches :as saved-searches]
            [clojure.set :as set]
            [clojure.string :as str]
            [core.front.admin]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.href :as href]
            [core.front.icons :as i]
            [core.front.session :as session]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf])
  (:require-macros [core.util.util :as util]))

(defn- search-group-ids [db]
  (not-empty (vec (get-in db [::state :group-ids]))))

(defn- map-view? [db]
  (= :dashboard-search/map-view (get-in db [::state :selected-view])))

(defn- restore-pending?
  "True while a Property restore must block normal search and initialization."
  [{{{:keys [entity-tab]} :pending-restore} ::saved-searches/state}]
  (= :property entity-tab))

(defn- cluster-results
  "Group the properties of a map cluster by address and state, for the cluster listing."
  [results]
  (->> results
       (group-by :property/primary-address)
       (p/map-vals (partial group-by :domain/state))))

(defn- render-clustered-results [address+results open-result-view tr _lang]
  (futil/for-frag [[address state+results] address+results]
    [:div.mb-3
     [:h4.uppercase.text-sm
      (or address (tr [:address/address-missing]))]
     (futil/for-frag [[state results] state+results]
       [:div.mt-2
        [:div.text-xs.font-bold (tr [state])]
        (futil/for-frag [{:property/keys [id] :as result} results]
          [:div.w-full
           [:button.p-3.bg-white.mt-2.w-full.border-0.text-left.select-search-result-button.flex.items-center
            {:on-click #(open-result-view result)}
            [:span.font-bold id]]])])]))

(def map-search-mode
  "Property search mode for `dashboard-map`. The map response carries the whole Elasticsearch
   document, so no `:search-command` for fetching a full result is needed."
  {:name                     :property
   :title                    :dashboard-search/property-title
   :map-search-command       :property/find-for-map
   :render-search-result     (fn [result {:keys [map-search?]}]
                               [sr/property-search-result-box result map-search?])
   :cluster-fn               cluster-results
   :render-clustered-results render-clustered-results})

(defn reset-state
  "Reset the search state that is owned by the view embedding the property search, so that a
   scope selected in one view does not leak into another one."
  [db]
  (-> db
      (dissoc ::property-search-filters/attribute-filters)
      (assoc-in [::state :state-filter] property-model/default-search-states)
      (update ::state dissoc
              :search-term
              :group-ids
              :results
              :total-count
              :selected-properties
              :sort-condition
              :selected-view
              :config
              :find-request-id
              :config-request-id)))

(rf/reg-event-fx ::init
  (fn [{:keys [db]} _]
    {:db (reset-state db)
     :fx [[:dispatch [::session/wait-for-user-data [::fetch-property-search-config]]]]}))

(rf/reg-event-fx ::fetch-property-search-config
  (fn [{{user :session/user :as db} :db} [_ search-event]]
    (let [admin?     (session/admin? user)
          group-ids  (search-group-ids db)
          request-id (random-uuid)]
      ;; Don't run on init for super admin, they have to select group first:
      (when (or (not admin?) (seq group-ids))
        ;; Keep the nil search-event slot: comm appends [status body request-id].
        {:db (assoc-in db [::state :config-request-id] request-id)
         :fx [[:dispatch [:comm/command-monitored
                          {:command          :user/property-search-config
                           ;; Monitoring settles the operation; the stored id still guards stale responses.
                           :operation-id     request-id
                           :data             (p/assoc-if {}
                                                         :domain-group-ids group-ids)
                           :on-success       [::fetch-property-search-config.success request-id search-event]
                           :on-error         [::fetch-property-search-config.error request-id]
                           :finally-dispatch [::fetch-property-search-config.done request-id]}]]]}))))

(rf/reg-event-fx ::fetch-property-search-config.success
  (fn [{:keys [db]} [_ request-id search-event _status {:keys [domain-group-ids] :as config}]]
    (when (= request-id (get-in db [::state :config-request-id]))
      {:db (assoc-in db [::state :config] config)
       :fx (into [(if search-event
                    [:dispatch search-event]
                    [:dispatch [::search]])]
                 (map (fn [group-id]
                        [:dispatch [::municipality/load-map-config group-id]]))
                 domain-group-ids)})))

(rf/reg-event-db ::fetch-property-search-config.done
  (fn [db [_ request-id status]]
    (if (and (not= 200 status)
             (= request-id (get-in db [::state :config-request-id]))
             (restore-pending? db))
      (p/dissoc-in db [::saved-searches/state :pending-restore])
      db)))

(rf/reg-event-fx ::fetch-property-search-config.error
  ;; Connection errors skip :finally-dispatch; :on-error replaces the default notification.
  (fn [_ [_ request-id]]
    {:fx [[:dispatch [::fetch-property-search-config.done request-id]]
          [:dispatch [:notifications/add :toast :error
                      :error/connection-error-title :error/connection-error-description]]]}))

(rf/reg-event-fx ::set-group-id
  (fn [{:keys [db]} [_ group-id]]
    {:db (assoc-in db [::state :group-ids] (some-> group-id vector))
     :fx [[:dispatch [::property-search-filters/reset-attribute-filters]]
          [:dispatch [::reset-search]]
          [:dispatch [::fetch-property-search-config]]]}))

;; Used when the domain group scope and the search term are owned by the surrounding view, e.g. by
;; the organization and search fields of the global search page.
(rf/reg-event-fx ::search-in-groups
  (fn [{:keys [db]} [_ group-ids search-term]]
    (when-not (restore-pending? db)
      (let [group-ids  (not-empty (vec (sort group-ids)))
            new-scope? (not= group-ids (search-group-ids db))
            db         (cond-> (assoc-in db [::state :group-ids] group-ids)
                         (str/blank? search-term)       (p/dissoc-in [::state :search-term])
                         (not (str/blank? search-term)) (assoc-in [::state :search-term] search-term))]
        {:db db
         :fx (if new-scope?
               ;; Another domain group scope may have different attribute templates, so the
               ;; configuration is refetched. It triggers the search once it has been resolved.
               [[:dispatch [::property-search-filters/reset-attribute-filters]]
                [:dispatch [::fetch-property-search-config]]]
               [[:dispatch [::search]]])}))))

(rf/reg-event-fx ::set-state-filter
  (fn [{:keys [db]} [_ state]]
    {:db (assoc-in db [::state :state-filter] state)
     :fx [[:dispatch [::search]]]}))

(rf/reg-event-fx ::clear-filters
  (fn [{:keys [db]} _]
    {:db (-> db
             (dissoc ::property-search-filters/attribute-filters)
             (assoc-in [::state :state-filter] #{}))
     :fx [[:dispatch [::search]]]}))

(defn- attribute-filters [db]
  (->> db
       ::property-search-filters/attribute-filters
       (remove #(-> % :value str/blank?))))

(defn- state-filter-values [db]
  (get-in db [::state :state-filter] property-model/default-search-states))

(defn map-search-request
  "Request data for `:property/find-for-map`, the map view counterpart of the result table search."
  [db]
  (p/assoc-if {:search-term "*"}
              :search-term (get-in db [::state :search-term])
              :filters (seq (attribute-filters db))
              :states (state-filter-values db)
              :domain-group-ids (search-group-ids db)))

(rf/reg-event-fx ::set-selected-view
  (fn [{:keys [db]} [_ view]]
    (when-not (restore-pending? db)
      {:db (assoc-in db [::state :selected-view] view)
       :fx [(if (= view :dashboard-search/map-view)
              ;; The map searches by itself once it has been loaded
              [:dispatch [::dashboard-map/init map-search-mode]]
              [:dispatch [::search]])]})))

(rf/reg-event-fx ::search
  (fn [{{{:keys [search-term config] :as state} ::state
         user                                   :session/user
         :as                                    db}           :db}
       [_ {:keys [sort-condition limit skip append?]}]]
    (when-not (restore-pending? db)
      (let [limit          (or limit
                               (:limit state)
                               (:limit config)
                               50)
            group-ids      (search-group-ids db)
            sort-condition (or sort-condition
                               (:sort-condition state)
                               (:sort-condition config))
            filters        (attribute-filters db)
            state-filter   (state-filter-values db)
            admin?         (session/admin? user)]
        (when (or (not admin?) (seq group-ids))
          (if (map-view? db)
            {:fx [[:dispatch [::dashboard-map/map-search (map-search-request db) nil]]]}
            (let [request-id (random-uuid)
                  db         (-> db
                                 (search-counts/start-request :property)
                                 (assoc-in [::state :sort-condition] sort-condition)
                                 (assoc-in [::state :find-request-id] request-id))]
              {:db db
               :fx [[:dispatch [:comm/command-monitored
                                {:command          :property/find
                                 :operation-id     request-id
                                 :data             (p/assoc-if {:search-term "*"
                                                                :skip        0}
                                                               :search-term search-term
                                                               :filters filters
                                                               :states state-filter
                                                               :skip skip
                                                               :limit limit
                                                               :domain-group-ids group-ids
                                                               :sort-condition sort-condition)
                                 :on-success       (search-counts/response-event
                                                     db :property [::find.success request-id append?])
                                 :on-error         (search-counts/error-event db :property)
                                 :finally-dispatch (search-counts/done-event db :property nil)}]]]})))))))

(rf/reg-event-fx ::search-more
  (fn [{:keys [db]} _]
    {:fx [[:dispatch [::search {:append? true
                                :skip    (-> db ::state :results count)}]]]}))

(rf/reg-event-fx ::reset-search
  (fn [{:keys [db]} _]
    {:db (p/dissoc-in db [::state :search-term])
     :fx [[:dispatch [::search]]]}))

(rf/reg-event-fx ::find.success
  (fn [{:keys [db]} [_ request-id append? _status {:keys [results total-count]}]]
    (when (= request-id (get-in db [::state :find-request-id]))
      {:db (-> (if append?
                 (update-in db [::state :results] (fnil into []) results)
                 (assoc-in db [::state :results] results))
               (assoc-in [::state :total-count] total-count))})))

(rf/reg-sub ::state :-> ::state)

(rf/reg-sub ::config
  :<- [::state]
  :-> :config)

(rf/reg-sub ::current-view
  :<- [::state]
  (fn [state _]
    (:selected-view state :dashboard-search/list-view)))

(rf/reg-sub ::map-center
  :<- [::config]
  :-> :default-map-center)

(rf/reg-sub ::domain-group-ids
  :<- [::config]
  :-> :domain-group-ids)

(rf/reg-sub ::map-search-opts
  :-> map-search-request)

(rf/reg-sub ::search-term
  :<- [::state]
  :-> :search-term)

(rf/reg-sub ::group-ids
  :<- [::state]
  :-> :group-ids)

(rf/reg-sub ::group-id
  :<- [::group-ids]
  :-> first)

(rf/reg-sub ::loading?
  :<- [:comm/command-processing? :property/find]
  :<- [:comm/command-processing? :user/property-search-config]
  (fn [[searching? config-loading?] _]
    (or searching? config-loading?)))

(rf/reg-sub ::state-filter
  :<- [::state]
  :-> :state-filter)

(rf/reg-sub ::property-states
  :<- [::config]
  :-> :property-states)

(rf/reg-sub ::results
  :<- [::state]
  (fn [state _]
    (->> (:results state)
         (sort-by :db/id >))))

(rf/reg-sub ::total-count
  :<- [::state]
  :<- [::results]
  (fn [[state results] _]
    ;; Every match, of which only the loaded ones are shown in the result table
    (:total-count state (count results))))

(rf/reg-sub ::result-count
  :<- [::current-view]
  :<- [::total-count]
  :<- [::dashboard-map/total-count]
  (fn [[view total-count map-total-count] _]
    (if (= view :dashboard-search/map-view)
      (or map-total-count 0)
      total-count)))

(rf/reg-sub ::more-results?
  :<- [::results]
  :<- [::total-count]
  (fn [[results total-count] _]
    (< (count results) total-count)))

(rf/reg-event-fx ::freetext-search-event
  (fn [{:keys [db]} [_ term]]
    {:dispatch-debounce [{:id      ::do-freetext-search
                          :timeout 500
                          :action  :dispatch
                          :event   [::search]}]
     :db                (if (str/blank? term)
                          (p/dissoc-in db [::state :search-term])
                          (assoc-in db [::state :search-term] term))}))

(rf/reg-event-db ::close-mass-delete-modal
  #(dissoc % ::mass-delete-modal-data))

(rf/reg-event-db ::set-property-mass-delete-state
  (fn [db [_ state]]
    (assoc-in db [::mass-delete-modal-data :mass-delete-modal-state] state)))

(rf/reg-event-db ::set-attribute-delete
  (fn [db [_ delete?]]
    (assoc-in db [::mass-delete-modal-data :delete-with-attributes?] delete?)))

(rf/reg-event-fx ::delete-properties
  (fn [{:keys [db]} [_ properties delete-with-attributes?]]
    {:db (assoc-in db [::mass-delete-modal-data :mass-delete-modal-state] :loading)
     :fx [[:comm/command
           {:command    :property/mass-delete-property-records
            :data       (util/mmap {:property-ids (->> properties
                                                       (map parse-long)
                                                       set)}
                                   delete-with-attributes?)
            :on-success [::delete-properties.success properties]}]]}))

(rf/reg-event-db ::delete-properties.success
  (fn [db [_ selections _ deletions]]
    ;; deletions also potentially contain deleted property attributes, so we need to check which of the deleted entities
    ;; match the selected elasticsearch ids
    (let [deleted-props    (->> deletions
                                keys
                                (map str)
                                set
                                (set/intersection selections))
          deleted-count    (count deleted-props)
          not-deleted-ids  (set/difference selections deleted-props)
          mass-delete-data (merge (::mass-delete-modal-data db) {:mass-delete-modal-state  :result
                                                                 :deleted-count            deleted-count
                                                                 :not-deleted-property-ids not-deleted-ids})]
      (-> db
          (assoc ::mass-delete-modal-data mass-delete-data)
          (assoc-in [::state :selected-properties] not-deleted-ids)
          (update-in [::state :total-count] #(some-> % (- deleted-count)))
          (update-in [::state :results] (fn [results] (remove #(contains? deleted-props (:id %)) results)))))))

(rf/reg-sub ::mass-delete-modal-data
  :-> ::mass-delete-modal-data)


(rf/reg-sub ::mass-delete-modal-state
  :<- [::mass-delete-modal-data]
  :-> :mass-delete-modal-state)

(rf/reg-sub ::mass-deleted-count
  :<- [::mass-delete-modal-data]
  :-> :deleted-count)

(rf/reg-sub ::not-deleted-property-ids
  :<- [::mass-delete-modal-data]
  :-> :not-deleted-property-ids)

(rf/reg-sub ::not-deleted-properties
  :<- [::not-deleted-property-ids]
  :<- [::results]
  (fn [[ids results] _]
    (->> results
         (filter #(contains? ids (:id %))))))

(rf/reg-sub ::delete-with-attributes?
  :<- [::mass-delete-modal-data]
  :-> :delete-with-attributes?)

(rf/reg-sub ::all-selected-properties-unverified?
  :<- [::selected-properties]
  :<- [::results]
  (fn [[selected-properties results] _]
    (if (empty? selected-properties)
      true ; No properties selected, not applicable
      (let [selected-ids-set (set selected-properties)
            selected-results (->> results
                                  (filter #(contains? selected-ids-set (:id %))))]
        ;; All selected properties must be present in results and unverified
        ;; If count doesn't match, some selected properties are missing from current view
        ;; Treat missing properties as "not eligible" to verify
        (and (= (count selected-results)
                (count selected-properties))
             (every? (du/in-state-fn :property.state/unverified) selected-results))))))


(rf/reg-event-db
  ::open-mass-verify
  (fn [db _]
    (assoc-in db [::mass-verify-modal-data :mass-verify-modal-state] :confirm)))

(rf/reg-event-db
  ::close-mass-verify-modal
  #(dissoc % ::mass-verify-modal-data))

(rf/reg-event-fx
  ::verify-properties
  (fn [{:keys [db]} [_ properties]]
    {:db (assoc-in db [::mass-verify-modal-data :mass-verify-modal-state] :loading)
     :fx [[:comm/command
           {:command          :property/set-property-verified
            :data             {:property-ids (vec (distinct (map parse-long properties)))}
            :on-success       [::verify-properties.success properties]
            :on-bad-request   [::verify-properties.conflict]
            :finally-dispatch [::verify-properties.finally]}]]}))

(rf/reg-event-db
  ::verify-properties.success
  (fn [db [_ properties _response]]
    (let [selected-ids (set properties)]
      (-> db
          (update-in [::state :results]
                     (fn [results]
                       (mapv (fn [property]
                               (if (contains? selected-ids (:id property))
                                 (-> property
                                     (assoc :property/verified-id (:property/id property))
                                     (assoc :domain/state :property.state/active))
                                 property))
                             results)))
          (assoc-in [::mass-verify-modal-data :mass-verify-modal-state] :result)
          (p/dissoc-in [::mass-verify-modal-data :conflicting-property-ids])))))

(rf/reg-event-db
  ::verify-properties.conflict
  (fn [db [_ _status body _request-id]]
    (let [conflicting-ids (-> body :conflicting-property-ids set)]
      (assoc-in db [::mass-verify-modal-data :conflicting-property-ids] conflicting-ids))))

(rf/reg-event-db
  ::verify-properties.finally
  (fn [db _]
    (let [state (get-in db [::mass-verify-modal-data :mass-verify-modal-state])]
      (if (= :loading state)
        ;; If there are conflicting properties, show the conflict state, otherwise go back to confirm
        (if (-> db (get-in [::mass-verify-modal-data :conflicting-property-ids]) seq)
          (assoc-in db [::mass-verify-modal-data :mass-verify-modal-state] :conflict)
          (assoc-in db [::mass-verify-modal-data :mass-verify-modal-state] :confirm))
        db))))

(rf/reg-sub
  ::mass-verify-modal-data
  :-> ::mass-verify-modal-data)

(rf/reg-sub
  ::mass-verify-modal-state
  :<- [::mass-verify-modal-data]
  :-> :mass-verify-modal-state)

(rf/reg-sub
  ::conflicting-property-ids
  :<- [::mass-verify-modal-data]
  :-> :conflicting-property-ids)



(defn- state-filter [tr class]
  (let [aggregated-states (<sub [::property-states])
        selected-states   (<sub [::state-filter])
        ;; Keep valid selected states visible even when they have no results.
        values            (->> (concat aggregated-states
                                       (filter property-model/property-states selected-states))
                               distinct
                               vec)]
    [:div {:class class}
     [ui-multi-select/multi-select {:id          "status-filter-select"
                                    :label-text  (tr [:property/state-filter])
                                    :hide-label? true
                                    :values      values
                                    :on-change   #(>evt [::set-state-filter %])
                                    :value       selected-states
                                    :val->str    #(tr [%])}]]))

(defn filter-controls
  "Attribute and state filters of the property search, laid out like the filters of the other tabs
   of the search page."
  [tr]
  (let [advanced? (<sub [:session/property-management-advanced?])
        templates (<sub [::property-attribute-templates])]
    [:<>
     [:div.flex.flex-wrap.-mx-2.items-start
      (when advanced?
        [:div {:class filter-common/condition-width-classes}
         [property-search-filters/attribute-select tr templates {:light?     false
                                                                 :class-name "full-width-select"}]])
      [state-filter tr filter-common/condition-width-classes]]
     (when advanced?
       [:div.mt-4
        [property-search-filters/attribute-filter-boxes tr templates [::search]]])
     (when (or (seq (<sub [::state-filter]))
               (seq (<sub [::property-search-filters/attribute-filters])))
       [ui/button {:kind       :ghost
                   :size       :small
                   :class-name "mt-2 font-semibold uppercase"
                   :data-cy    "clear-property-filters"
                   :on-click   #(>evt [::clear-filters])}
        (tr [:clear])])]))

(defn- search [tr loading?]
  [:div.bg-ui-light-gray.flex.flex-col.rounded-lg.p-4
   [:h3.bold (tr [:search/search])]
   [ui/search {:id                      "property-dashboard-search"
               :class                   "h-4"
               :label-text              (tr [:dashboard-search/search])
               :placeholder             (tr [:dashboard-search/search-placeholder])
               :close-button-label-text (tr [:search/empty])
               :value                   (<sub [::search-term])
               :on-change               #(rf/dispatch-sync [::freetext-search-event (-> % futil/tval str)])
               :search-in-progress?     loading?
               :loading-style           {:top "3px"}
               :loading-text            (tr [:search/loading])
               :clear-fn                #(>evt [::reset-search])
               :light                   true}]
   [:div.flex.flex-col
    [:div.flex.items-start
     (when (<sub [:session/property-management-advanced?])
       [property-search-filters/filters tr (<sub [::property-attribute-templates]) [::search] {:light? true}])
     [state-filter tr "ml-auto mt-4 w-full md:w-1/2 lg:w-1/3 xl:w-1/4"]]]])

(defn results-header
  "Displays the number of results found in parenthesis as 'Results (<nmb_results>)'."
  [tr]
  [:h3.bold.mb-0 (tr [:search/results-with-count] [(<sub [::result-count])])])

(defn- on-sort-hook [attribute previous-sort-order]
  (let [sort-order (case previous-sort-order
                     :asc :desc
                     :desc :asc
                     nil :asc)]
    (>evt [::search {:sort-condition [attribute sort-order]}])))

(defn- sorting-opts [config attribute]
  (when (some #(when (= % attribute) %) (:sortable-attrs config))
    {:sorting      true
     :on-sort-hook (partial on-sort-hook attribute)}))

(rf/reg-sub ::property-attribute-templates
  :<- [::config]
  :-> :property-attribute-templates)

(defn sortable-cols [config tr]
  (some->> config
           :sortable-attrs
           (map (fn [attribute]
                  [attribute
                   (if (= :domain/state attribute)
                     (comp tr vector attribute)
                     attribute)
                   ;; A stable sort key keeps the sorted column's identity even though the
                   ;; translated display extractor of :domain/state is recreated on re-render.
                   (assoc (sorting-opts config attribute) :sort-key attribute)]))
           (into [])))

(rf/reg-sub ::sortable-cols
  :<- [::config]
  :<- [:tr]
  (fn [[config tr] _]
    (sortable-cols config tr)))

(rf/reg-sub ::property-attribute-cols
  :<- [::property-attribute-templates]
  :<- [:language]
  :<- [:tr]
  :<- [::date-util/date-fn]
  (fn [[property-attribute-templates lang tr format-date] _]
    (some->> property-attribute-templates
             (sort-by (juxt :ordered/order
                            #(property-model/attribute-template-name % lang)))
             (map (fn [{:db/keys [id] :as template}]
                    [(property-model/attribute-template-name template lang)
                     (fn [{:keys [property/attributes]}]
                       (when-let [attr (p/find-first attributes {:template-id (str id)})]
                         (property-model/attribute-value template attr lang tr format-date)))
                     {}])))))

(rf/reg-sub ::ff-periodic-inspection
  :<- [::property-dashboard-subs/authority-groups-with-periodic-inspections]
  (fn [authority-groups _]
    (boolean (seq authority-groups))))

(rf/reg-sub ::multi-select-properties-enabled?
  :<- [::ff-periodic-inspection]
  :<- [::ff/enabled? :feature-flag.id/multi-select-properties-for-periodic-workspace-creations]
  :<- [::ff/enabled? :feature-flag.id/mass-delete-empty-properties]
  (fn [[periodic-inspections-enabled? multi-select-enabled? mass-delete-enabled?] _]
    (and multi-select-enabled?
         (or periodic-inspections-enabled?
             mass-delete-enabled?))))

(rf/reg-sub ::cols
  :<- [::sortable-cols]
  :<- [::property-attribute-cols]
  :<- [:session/property-management-advanced?]
  (fn [[config-cols property-attr-cols advanced?] _]
    (if advanced?
      (concat config-cols property-attr-cols)
      config-cols)))

(rf/reg-sub ::selected-properties
  :<- [::state]
  :-> :selected-properties)

(rf/reg-sub ::sort-condition
  :<- [::state]
  :-> :sort-condition)

(rf/reg-sub ::initial-sorting
  :<- [::sort-condition]
  :<- [::config]
  (fn [[sort-condition config] _]
    (or sort-condition
        (some-> config :sort-condition))))

(rf/reg-event-db ::toggle-property-selection
  (fn [db [_ checked? property-id]]
    (if checked?
      (update-in db [::state :selected-properties] (fnil conj #{}) property-id)
      (update-in db [::state :selected-properties] disj property-id))))

(rf/reg-event-db ::clear-selected-properties
  (fn [db _]
    (assoc-in db [::state :selected-properties] #{})))

(defn- mass-actions
  "Actions that operate on the selected properties in the table toolbar."
  [tr]
  (let [selected-properties (<sub [::selected-properties])
        disabled?           (empty? selected-properties)]
    (concat
      (when (<sub [::ff-periodic-inspection])
        [{:title     (tr [:property/create-periodic-inspection])
          :on-click  #(>evt [::new-periodic-inspection/open-periodic-inspection-modal nil])
          :icon      Add16
          :disabled? disabled?}])
      (when (<sub [::ff/enabled? :feature-flag.id/mass-delete-empty-properties])
        [{:title     (tr [:property/mass-delete-button])
          :on-click  #(>evt [::set-property-mass-delete-state :confirm])
          :icon      i/trash
          :disabled? (or disabled?
                         (property-model/deletion-count-over-limit? (count selected-properties)))}])
      (when (and (<sub [::ff/enabled? :feature-flag.id/property-verification])
                 (<sub [:session/property-manager?]))
        [{:title     (tr [:property/mass-verify-button])
          :on-click  #(>evt [::open-mass-verify])
          :icon      i/checkbox-on
          :disabled? disabled?}]))))

(defn- table [tr loading?]
  [sortable-table/table
   (merge
     {:id                  "property-search-results"
      :initial-sorting     (<sub [::initial-sorting])
      :cols                (<sub [::cols])
      :configurable?       true
      :empty-placeholder   (if loading?
                             (cc/loading-spinner {:with-overlay false})
                             (tr [:property/empty-search-placeholder]))
      :id-func             :id
      :items               (<sub [::results])
      :link                {:description (tr [:open])
                            :make-href   #(href/href :view/property
                                                     {:db-id (:id %)})}
      :selection           (<sub [::selected-properties])
      ;; TODO move check inside action collection once there are more actions / expanded content
      :make-actions        (when (<sub [::ff-periodic-inspection])
                             (fn [property]
                               [{:title    (tr [:property/create-periodic-inspection])
                                 :on-click #(>evt [::new-periodic-inspection/open-periodic-inspection-modal
                                                   (-> property :id js/parseInt)])
                                 :icon     Add16}]))
      :toolbar-actions     (mass-actions tr)
      :table-toolbar-props {:class "mb-4"}
      :tr                  tr
      :sticky-header?      true}
     (when (<sub [::multi-select-properties-enabled?])
       {:on-select (fn [checked? id]
                     (>evt [::toggle-property-selection checked? id]))
        :on-clear  #(>evt [::clear-selected-properties])}))])

(defn- search-more-button [tr loading?]
  (when (<sub [::more-results?])
    [:div.flex.justify-center
     (if loading?
       (cc/loading-spinner {:with-overlay false
                            :small        true})
       [ui/button
        {:on-click #(>evt [::search-more])
         :disabled loading?}
        (tr [:show-more])])]))

(defn- group-selector [tr]
  [:div.max-w-lg
   [acommon/group-selector {:title-text          (tr [:property-view/select-group])
                            :tr                  tr
                            :on-change           #(>evt [::set-group-id (:id %)])
                            :groups-subscription [:core.front.admin/domain-groups]
                            :class-name          "[&_input]:!bg-ui-white"}]])

(defn- mass-delete-modal [tr]
  (let [state                   (<sub [::mass-delete-modal-state])
        open?                   (some? state)
        delete-with-attributes? (or (<sub [::delete-with-attributes?])
                                    false)
        close-modal-fn          #(>evt [::close-mass-delete-modal])
        selected-properties     (<sub [::selected-properties])
        confirm-state?          (= state :confirm)
        loading-state?          (= state :loading)
        deleted-count           (<sub [::mass-deleted-count])
        selected-count          (count selected-properties)
        not-deleted-properties  (<sub [::not-deleted-properties])
        not-deleted-count       (count not-deleted-properties)]
    [ui-modal/composed-input-modal
     {:id       "mass-delete-modal"
      :open     open?
      :on-close close-modal-fn
      :size     :md}
     (cc/modal-header {:close-modal close-modal-fn
                       :title       (cond
                                      confirm-state? (tr [:property/mass-delete-confirm-title])
                                      loading-state? (tr [:property/mass-delete-deleting-title])
                                      (= deleted-count 1) (tr [:property/mass-delete-result-title-single])
                                      :else (tr [:property/mass-delete-result-title-multi] [deleted-count]))})

     (when open?
       (if loading-state?
         (cc/skeleton-text {:paragraph true})
         (cc/modal-body
           {}
           (if confirm-state?
             [:div.flex.flex-col
              [:span
               (if (= selected-count 1)
                 (tr [:property/mass-delete-confirm-single])
                 (tr [:property/mass-delete-confirm-multi] [selected-count]))]
              (cc/checkbox {:id         "attribute-delete-toggle"
                            :label-text (tr [:property/mass-delete-attribute-confirm])
                            :checked    delete-with-attributes?
                            :on-change  #(>evt [::set-attribute-delete (not delete-with-attributes?)])})]

             (when (pos? not-deleted-count)
               [:div.flex.flex-col
                [:span
                 (if (= not-deleted-count 1)
                   (tr [:property/mass-delete-result-single])
                   (tr [:property/mass-delete-result-multi] [not-deleted-count]))]
                [:span (tr [:property/mass-delete-result-info])]
                [:table.properties
                 [:thead
                  [:tr
                   [:th (tr [:property/id])]
                   [:th (tr [:property/address])]]]
                 (into [:tbody]
                       (for [property not-deleted-properties]
                         [:tr
                          [:td (:property/id property)]
                          [:td (:property/primary-address property)]]))]])))))

     (cc/modal-footer
       {}
       (cc/button
         {:kind     :secondary
          :on-click close-modal-fn}
         (if confirm-state?
           (tr [:no])
           (tr [:close])))
       (when confirm-state?
         (cc/button
           {:kind     :primary
            :on-click #(>evt [::delete-properties selected-properties delete-with-attributes?])
            :disabled loading-state?}
           (tr [:yes]))))]))


(defn- mass-verify-modal [tr]
  (let [state                    (<sub [::mass-verify-modal-state])
        open?                    (some? state)
        close-modal-fn           #(>evt [::close-mass-verify-modal])
        selected-properties      (<sub [::selected-properties])
        all-selected-unverified? (<sub [::all-selected-properties-unverified?])
        conflicting-ids          (<sub [::conflicting-property-ids])
        confirm-state?           (= state :confirm)
        loading-state?           (= state :loading)
        result-state?            (= state :result)
        conflict-state?          (= state :conflict)
        show-warning?            (and confirm-state?
                                      (not all-selected-unverified?))
        selected-count           (count selected-properties)
        conflicting-count        (count conflicting-ids)]
    [ui-modal/composed-input-modal
     {:id       "mass-verify-modal"
      :open     open?
      :on-close close-modal-fn
      :size     :md}
     (cc/modal-header {:close-modal close-modal-fn
                       :title       (cond
                                      confirm-state?  (tr [:property/mass-verify-confirm-title])
                                      loading-state?  (tr [:property/mass-verify-confirm-title])
                                      conflict-state? (tr [:property/mass-verify-conflict-title])
                                      result-state?   (tr [:property/mass-verify-result-title]))})

     (when open?
       (if loading-state?
         (cc/skeleton-text {:paragraph true})
         (cc/modal-body
           {}
           (cond
             confirm-state?
             [:div.flex.flex-col
              [:span
               (if (= selected-count 1)
                 (tr [:property/mass-verify-confirm-single])
                 (tr [:property/mass-verify-confirm-multi] [selected-count]))]
              (when show-warning?
                [:span.text-support-error.mt-4
                 (tr [:property/mass-verify-warning-message])])]

             conflict-state?
             [:div.flex.flex-col
              [:span.text-support-error.font-semibold.mb-4
               (if (= conflicting-count 1)
                 (tr [:property/mass-verify-conflict-single])
                 (tr [:property/mass-verify-conflict-multi] [conflicting-count]))]
              [:div.flex.flex-col.gap-2
               (for [id conflicting-ids]
                 [:span.font-mono {:key id} id])]]

             result-state?
             [:div.flex.flex-col
              [:span
               (if (= selected-count 1)
                 (tr [:property/mass-verify-result-single])
                 (tr [:property/mass-verify-result-multi] [selected-count]))]]))))

     (cc/modal-footer
       {}
       (cc/button
         {:kind     :secondary
          :on-click close-modal-fn}
         (if confirm-state?
           (tr [:no])
           (tr [:close])))
       (when confirm-state?
         (cc/button
           {:kind     :primary
            :on-click #(>evt [::verify-properties selected-properties])
            :disabled (or loading-state?
                          show-warning?)}
           (tr [:yes]))))]))

(defn result-table
  "Result table and the modals for its actions."
  [tr]
  (let [loading? (<sub [::loading?])]
    [:<>
     [mass-delete-modal tr]
     (when (<sub [::ff/enabled? :feature-flag.id/property-verification])
       [mass-verify-modal tr])
     [new-periodic-inspection/inspections-created-modal tr]
     [new-periodic-inspection/periodic-inspection-modal tr]
     [:div.flex.flex-col.gap-4
      [table tr loading?]
      [search-more-button tr loading?]]]))

(defn map-results
  "Property results on a map, with the details of the selected result or cluster beside it."
  [tr]
  (when-let [map-center (<sub [::map-center])]
    [dashboard-map/map-and-details (util/mmap {:search-opts      (<sub [::map-search-opts])
                                               :domain-group-ids (<sub [::domain-group-ids])}
                                              tr
                                              map-center)]))

(defn results
  "Result count and result table, as shown on the property dashboard. The properties tab of the
   global search combines `results-header`, `result-table` and `map-results` of its own."
  [tr]
  [:div.flex.flex-col.gap-4
   [results-header tr]
   [result-table tr]])

(defn view [tr]
  (let [admin?    (<sub [:session/admin?])
        group-ids (<sub [::group-ids])]
    [:<>
     (when admin? [group-selector tr])
     (when (or (not admin?) (seq group-ids))
       [:section#property-dashboard-search
        [:div.flex.flex-col.gap-4
         [search tr (<sub [::loading?])]
         [results tr]]])]))
